import { useContext, useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material';
import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import { OfflineContext } from '../contexts/OfflineContext';
import { palette, typography, theme } from '../theme/colors';
import { ColorModeContext } from '../contexts/ColorModeContext';
import { uploadQueue } from '../lib/sync-manager';

/**
 * OfflineIndicator component
 *
 * Displays a visual indicator when the device is offline (no network connection).
 * The indicator automatically shows when offline and hides when online.
 * Uses Material-UI styling following the application's industrial design patterns.
 *
 * @example
 * ```tsx
 * <OfflineIndicator />
 * ```
 */
export const OfflineIndicator = () => {
  const { isOffline, isOnline, getQueuedTransactions } = useContext(OfflineContext);
  const { mode } = useContext(ColorModeContext);
  const [queueCount, setQueueCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  const colors = mode === 'dark' ? {
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
  } : {
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
  };

  // Handle manual sync
  const handleManualSync = async () => {
    if (isSyncing || !isOnline || queueCount === 0) {
      return;
    }

    setIsSyncing(true);
    try {
      await uploadQueue();
      // Refresh queue count after sync
      const transactions = await getQueuedTransactions();
      setQueueCount(transactions.length);
    } catch (error) {
      // Error handling - could show a toast notification here
    } finally {
      setIsSyncing(false);
    }
  };

  // Fetch queue count when offline
  useEffect(() => {
    const fetchQueueCount = async () => {
      if (isOffline) {
        const transactions = await getQueuedTransactions();
        setQueueCount(transactions.length);
      } else {
        setQueueCount(0);
      }
    };

    fetchQueueCount();

    // Poll for queue count updates every 5 seconds
    const interval = setInterval(fetchQueueCount, 5000);

    return () => clearInterval(interval);
  }, [isOffline, getQueuedTransactions]);

  // Don't render anything when online
  if (!isOffline) {
    return null;
  }

  const iconButtonStyles = {
    width: 28,
    height: 28,
    borderRadius: '6px',
    backgroundColor: 'transparent',
    color: palette.error,
    transition: theme.transitions.fast,
    '&:hover': {
      backgroundColor: alpha(palette.error, 0.15),
      color: palette.error,
    },
    '&:disabled': {
      color: alpha(palette.error, 0.4),
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        backgroundColor: alpha(palette.error, 0.1),
        border: `2px solid ${palette.error}`,
        borderRadius: theme.radius.xs,
      }}
    >
      <Badge
        badgeContent={queueCount}
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: palette.warning,
            color: '#FFFFFF',
            fontFamily: typography.fontFamily.mono,
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.bold,
            minWidth: 18,
            height: 18,
            borderRadius: theme.radius.xs,
            border: `2px solid ${colors.surface}`,
          },
        }}
      >
        <CloudOffOutlinedIcon
          sx={{
            color: palette.error,
            fontSize: 20
          }}
        />
      </Badge>
      <Typography
        variant="body2"
        sx={{
          fontFamily: typography.fontFamily.mono,
          fontWeight: typography.fontWeight.bold,
          color: palette.error,
          textTransform: 'uppercase',
          fontSize: typography.fontSize.xs,
          letterSpacing: typography.letterSpacing.wide,
        }}
      >
        OFFLINE
      </Typography>

      {/* Manual Sync Button */}
      {queueCount > 0 && isOnline && (
        <Tooltip title={isSyncing ? 'SYNCING...' : 'SYNC QUEUED TRANSACTIONS'}>
          <span>
            <IconButton
              onClick={handleManualSync}
              disabled={isSyncing}
              sx={iconButtonStyles}
            >
              {isSyncing ? (
                <CircularProgress
                  size={14}
                  sx={{
                    color: palette.error,
                  }}
                />
              ) : (
                <SyncOutlinedIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};
